import { useEffect, useMemo, useState, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "focus-visible/dist/focus-visible";
import { AppRoutes, $Routes } from "@routes";
import {
  FullPageLoader,
  ZoomAppAuthState,
  TabChangeDisconnectModal,
  MainContainer,
} from "@components";
import {
  useHandleIconClick,
  useSocketConnections,
  useNotifications,
  useMeeting,
  useSdk,
  useProfile,
  useActiveWebMeetingCheck,
} from "@hooks";
import { resetPointsThrottle } from "@hooks/useNotifications";
import {
  initiateSocket,
  iconChat,
  notification,
  disconnectSocket,
} from "@socket";
import { socketInitiated } from "@store/customActions";
import { updateCoins } from "@store/engagementSlice";
import { useGetAllIconsQuery } from "@store/api/icon";
import { LoadGoogleAnalytics, isZoomApp } from "@utils/helpers";
import {
  selectAnalyticsData,
  selectNormalizedMeetingState,
} from "@store/selectors/meetingSelectors";
import {
  ActivityParticipantModal,
  ActivityPrepModal,
} from "@components/Activity";
import { hasDemoGuestToken } from "@utils/guestDemo";
import { APP_TYPE } from "@constants";

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { handleAddNotification } = useNotifications();
  const {
    hasToken,
    isAuthenticated,
    profileId,
    profile,
    hasFetchProfileError,
    isFetchingProfile,
  } = useProfile();
  const { userContextStatus, isAuthorized, appType } = useSdk();
  // Use normalized selectors for platform-agnostic access
  const analytics = useSelector(selectAnalyticsData);
  const normalizedMeeting = useSelector(selectNormalizedMeetingState);

  const { setTriggerLiveAnaltyics } = useSocketConnections();
  const { broadcastIconClick } = useHandleIconClick();
  useGetAllIconsQuery(null, {
    skip: appType === APP_TYPE.ZoomApp ? !profileId : !profileId || !hasToken,
  });
  const { isMeetingActive, isHostOrCoHost, isFetchingMeeting } = useMeeting();
  const liveActivity = useSelector((state) => state.activity.liveActivity);
  const showResult = useSelector((state) => state.activity.showResult);
  // Suppress the participant "Get Ready" overlay during a demo (a guest has no
  // profile, so it would otherwise fire on the host's screen).
  const isDemoActive = useSelector((state) => state.demo.isActive);

  // Extract normalized meeting data
  const meetingHostId = normalizedMeeting.hostId;
  const meetingUuid =
    normalizedMeeting.platform === "ZOOM"
      ? normalizedMeeting.platformData?.meetingObject?._id
      : normalizedMeeting.meetingInstanceId;
  const meetingInstanceId = normalizedMeeting.meetingInstanceId;
  const [showPrepModal, setShowPrepModal] = useState(true);

  // Phase 2 (guest demo): an anonymous visitor running the demo has no profile.
  // The socket must still connect, keyed by the demo agenda's host id, so the
  // guest joins the meeting room and receives the simulated participants/waves.
  const isGuestDemo = hasDemoGuestToken();
  const socketUserId = isAuthenticated ? profileId : meetingHostId;

  const isProfileLoading = isZoomApp
    ? isFetchingMeeting || !profileId || isFetchingProfile
    : isFetchingProfile;

  // Check for active web meetings
  const { isChecking: isCheckingActiveMeeting } = useActiveWebMeetingCheck();

  useEffect(() => {
    if (isMeetingActive && liveActivity.live) {
      const timer = setTimeout(() => {
        setShowPrepModal(false);
      }, 5000);

      return () => {
        clearTimeout(timer);
        setShowPrepModal(true);
      };
    }
  }, [isMeetingActive, liveActivity.live]);

  // TODO: clean up and remove useSocketConnection hook
  useEffect(() => {
    if (analytics.live) {
      setTriggerLiveAnaltyics(true);
    } else {
      setTriggerLiveAnaltyics(false);
    }
  }, [analytics.live, setTriggerLiveAnaltyics]);

  useEffect(() => {
    if (isAuthenticated && profileId && process.env.NODE_ENV === "production") {
      window.clarity?.("identify", profileId);
    }
  }, [isAuthenticated, profileId]);

  useEffect(() => {
    if (!isAuthenticated && !isGuestDemo) {
      console.info("User is not authenticated");
      return;
    }

    // For Zoom App: Connect socket immediately when meetingInstanceId is available
    // For Web App: Only connect socket manually after starting a meeting (handled in agenda actions)
    if (isZoomApp) {
      if (!meetingInstanceId) {
        console.info("Zoom App: No meetingInstanceId available yet");
        return;
      }
    } else {
      // For web users, socket connection is handled in handleStartWebMeeting
      // Only connect here if meetingInstanceId exists (user already started a meeting)
      if (!meetingInstanceId) {
        console.info(
          "Web App: No meetingInstanceId - socket will connect when meeting is started",
        );
        return;
      }
    }

    if (!location.pathname.includes($Routes.sendToolRedirectPage)) {
      console.info(
        `${isZoomApp ? "Zoom App" : "Web App"}: Initiating socket connection`,
        {
          socketUserId,
          meetingInstanceId,
        },
      );

      initiateSocket(socketUserId, meetingInstanceId);
      // TODO: Remove meetingUuid if not used
      dispatch(
        socketInitiated({
          meetingUuid,
          profileId: socketUserId,
          isHostOrCohost: isHostOrCoHost(socketUserId),
        }),
      );

      // TODO: clean up iconChat and notification subscription
      iconChat.subscribe((data) => {
        broadcastIconClick({ iconId: data.iconId, color: data.color });
      });

      notification.subscribe((data) => {
        const { type, meta } = data;

        if (type === "coins") {
          dispatch(updateCoins({ coins: meta?.value }));
        }

        handleAddNotification(data);
      });
    }

    return () => {
      iconChat.unsubscribe();
      notification.unsubscribe();
      resetPointsThrottle();
      disconnectSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    profileId,
    socketUserId,
    isGuestDemo,
    meetingHostId,
    meetingUuid,
    meetingInstanceId,
    isAuthenticated,
    broadcastIconClick,
    dispatch,
    handleAddNotification,
  ]);

  const renderAppComponents = useMemo(() => {
    if (isProfileLoading || isCheckingActiveMeeting) {
      return <FullPageLoader />;
    }

    if (
      appType === APP_TYPE.ZoomApp &&
      (!isAuthorized || hasFetchProfileError)
    ) {
      return <ZoomAppAuthState userContextStatus={userContextStatus} />;
    }

    return (
      <>
        {/* {!isZoomApp && <TabChangeDisconnectModal />} */}
        {isMeetingActive && (liveActivity.live || showResult) && (
          <>
            {showPrepModal &&
            liveActivity.live &&
            !isHostOrCoHost(profileId) &&
            !isDemoActive ? (
              <ActivityPrepModal />
            ) : (
              <ActivityParticipantModal />
            )}
          </>
        )}
        <Suspense fallback={<FullPageLoader />}>
          <AppRoutes />
        </Suspense>
      </>
    );
  }, [
    appType,
    hasFetchProfileError,
    isProfileLoading,
    isCheckingActiveMeeting,
    isAuthorized,
    isMeetingActive,
    liveActivity.live,
    showResult,
    userContextStatus,
    showPrepModal,
    isHostOrCoHost,
    profileId,
    isDemoActive,
  ]);

  return (
    <HelmetProvider>
      <LoadGoogleAnalytics />
      <MainContainer>{renderAppComponents}</MainContainer>
    </HelmetProvider>
  );
}

export default App;
